import { PAGE_PERMISSION_KEYS as P } from "@/lib/data/static/permissionOptions";

type Page = [path: string, ...permissions: string[]];
type HasPagePermission = (permission: string) => boolean;

// Keep this order aligned with the visible submenu leaves, including collapsed groups.
const sectionPages: Partial<Record<string, Page[]>> = {
  Dashboard: [["/", P.dashboard]],
  Intake: [
    ["/opportunities", P.intakeOpportunities],
    ["/leads", P.intakeLeads],
    ["/settings/intake/lead-sources", P.intakeSettingsLeadSources],
    ["/settings/intake/roles", P.intakeSettingsRoles],
    ["/settings/intake/teams", P.intakeSettingsTeams],
    ["/settings/intake/matter-types", P.intakeSettingsTypes],
    ["/settings/intake/statuses", P.intakeSettingsStatuses],
    ["/settings/intake/tags", P.intakeSettingsTags],
    ["/settings/intake/task-plans", P.intakeSettingsTaskPlans],
  ],
  Matters: [
    ["/matters/my-matters", P.mattersMy],
    ["/matters", P.mattersAll],
    ["/reports/closed-matters", P.mattersClosed],
    [
      "/reports/case-assignments",
      P.mattersReportsRoleAssignments,
      P.mattersReportsSettlements,
    ],
    ["/reports/demands", P.mattersReportsDemands],
    ["/reports/demand-summary", P.mattersReportsDemands],
    [
      "/reports/attorney-summary",
      P.mattersReportsRoleAssignments,
      P.mattersReportsSettlements,
    ],
    ["/reports/settlement-summary", P.mattersReportsSettlements],
    ["/reports/record-requests", P.mattersReportsRecordRequests],
    ["/reports/approaching-statutes", P.mattersReportsApproachingStatutes],
    ["/reports/matter-activity", P.mattersReportsActivity],
    ["/reports/matter-status-count", P.mattersReportsPerStatus],
    ["/reports/role-assignments", P.mattersReportsRoleAssignments],
    [
      "/matters/settings/scheduled-messages",
      P.mattersSettingsScheduledMessages,
    ],
    ["/matters/settings/intake-templates", P.mattersSettingsIntakeTemplates],
    ["/matters/settings/roles", P.mattersSettingsRoles],
    ["/matters/settings/teams", P.mattersSettingsTeams],
    ["/matters/settings/matter-types", P.mattersSettingsTypes],
    ["/matters/settings/statuses", P.mattersSettingsStatuses],
    ["/matters/settings/tags", P.mattersSettingsTags],
    ["/matters/settings/costs", P.mattersSettingsCosts],
    ["/matters/settings/task-plans", P.mattersSettingsTaskPlans],
    ["/matters/settings/phase-projects", P.mattersSettingsPhaseProjects],
    ["/matters/settings/litigation-plans", P.mattersSettingsLitigationPlans],
    ["/matters/settings/files", P.mattersSettingsDefaultFiles],
  ],
  Referrals: [
    ["/referrals", P.referralsAll],
    ["/referrals/partner-firms", P.referralsAll],
    ["/referrals/directory-profile", P.referralsSettings],
    ["/referrals/settings/roles", P.referralsSettingsRoles],
    ["/referrals/settings/teams", P.referralsSettingsTeams],
    ["/referrals/settings/matter-types", P.referralsSettingsTypes],
    ["/referrals/settings/statuses", P.referralsSettingsStatuses],
    ["/referrals/settings/tags", P.referralsSettingsTags],
    ["/referrals/settings/task-plans", P.referralsSettingsTaskPlans],
  ],
  "Mass Torts": [["/mass-torts", P.massTortsAll]],
  Files: [["/files"]],
  "Signature Requests": [["/signature-requests", P.signatureRequestsAccess]],
  Contacts: [
    ["/contacts", P.contactsAll],
    ["/settings/contact", P.contactsSettingsTags],
  ],
  Apps: [
    ["/apps/app-library", P.appsAccess],
    ["/apps/my-apps", P.appsAccess],
  ],
  Firm: [
    ["/settings/firm", P.firmMyFirm],
    ["/settings/files", P.firmFiles],
    ["/charges", "owner"],
    ["/settings/templates", P.firmTemplates],
    ["/reports/user-productivity", P.firmReportsUserActivity],
    ["/settings/users", P.firmSettingsUsers],
    ["/settings/communication", P.firmSettingsPhoneNumbers],
  ],
};

const matterPages: Page[] = [
  ["/", P.mattersDetailOverview],
  ["/matter-details", P.mattersDetailParties],
  ["/incident-details", P.mattersDetailIncidentDetails],
  ["/custom-fields", P.mattersDetailCustomFields],
  ["/property-damage", P.mattersDetailPropertyDamage],
  ["/emails", P.mattersDetailEmails],
  ["/liens", P.mattersDetailLiens],
  ["/costs", P.mattersDetailCosts],
  ["/charges", "owner"],
  ["/medical-treatments", P.mattersDetailMedicalTreatments],
  ["/settlements", P.mattersDetailSettlements],
  ["/litigation", P.mattersDetailLitigation],
  ["/tasks", P.mattersDetailTasks],
  ["/notes", P.mattersDetailNotes],
  ["/updates", P.mattersDetailUpdates],
  ["/files", P.mattersDetailFiles],
  ["/signature-requests", P.mattersDetailSignatureRequests],
];

const massTortPages: Page[] = [
  ["/", P.massTortsDetailOverview],
  ["/tasks", P.massTortsDetailTasks],
  ["/surveys", P.massTortsDetailSurveys],
  ["/custom-fields", P.massTortsDetailCustomFields],
  ["/notes", P.massTortsDetailNotes],
  ["/file-generation", P.massTortsDetailFileGeneration],
  ["/files", P.massTortsDetailFiles],
];

// These routes are still reachable within Matters, but aren't separate menu leaves.
const matterPageAliases: Page[] = [
  ["/settings/matter-defaults", P.mattersSettingsIntakeTemplates],
  ["/reports/open-matters", P.mattersAll],
  ["/reports/settlements", P.mattersReportsSettlements],
];

const matchesPage = (pathname: string, page: string) =>
  pathname === page || (page !== "/" && pathname.startsWith(`${page}/`));

const canOpen = (
  page: Page,
  hasPermission: HasPagePermission,
  owner: boolean,
) =>
  page
    .slice(1)
    .every((permission) =>
      permission === "owner" ? owner : hasPermission(permission),
    );

export function getFirstSidebarPage(
  section: string,
  hasPermission: HasPagePermission,
  owner: boolean,
) {
  return sectionPages[section]?.find((page) =>
    canOpen(page, hasPermission, owner),
  )?.[0];
}

export function canRestoreSidebarPage(
  section: string,
  pathname: string,
  hasPermission: HasPagePermission,
  owner: boolean,
) {
  const path = pathname.replace(/\/+$/, "") || "/";
  const exactPage = sectionPages[section]?.find(([to]) => to === path);
  if (exactPage) return canOpen(exactPage, hasPermission, owner);
  const alias =
    section === "Matters" &&
    matterPageAliases.find(([to]) => matchesPage(path, to));
  if (alias) return canOpen(alias, hasPermission, owner);

  const matterMatch =
    /^\/matters\/(?!settings(?:\/|$)|my-matters(?:\/|$))[^/]+(\/.*)?$/.exec(
      path,
    );
  if ((section === "Matters" || section === "Mass Torts") && matterMatch) {
    const page = matterPages.find(([to]) =>
      matchesPage(matterMatch[1] || "/", to),
    );
    return Boolean(
      page &&
        hasPermission(P.mattersDetail) &&
        canOpen(page, hasPermission, owner),
    );
  }
  const massTortMatch = /^\/mass-torts\/[^/]+(\/.*)?$/.exec(path);
  if (section === "Mass Torts" && massTortMatch) {
    const page = massTortPages.find(([to]) =>
      matchesPage(massTortMatch[1] || "/", to),
    );
    return Boolean(
      page &&
        hasPermission(P.massTortsDetail) &&
        canOpen(page, hasPermission, owner),
    );
  }
  if (section === "Contacts" && path.startsWith("/contacts/")) {
    return hasPermission(P.contactsDetail);
  }
  if (section === "Firm" && path === "/profile") return true;

  // Other pages beneath a static destination use that destination's grant.
  const page = sectionPages[section]
    ?.filter(([to]) => to !== "/matters" && to !== "/mass-torts")
    .sort((a, b) => b[0].length - a[0].length)
    .find(([to]) => matchesPage(path, to));
  return Boolean(page && canOpen(page, hasPermission, owner));
}
